<template>
  <div class="video-publish-page">
    <div class="page-header">
      <h2 class="page-title">发布视频</h2>
    </div>
    <div class="page-divider"></div>
    <div class="filter-bar">
      <el-date-picker v-model="dateRange" type="daterange" range-separator="至" start-placeholder="开始时间"
        end-placeholder="结束时间" value-format="YYYY-MM-DD" class="filter-item filter-date" />
      <el-select v-model="filters.taskId" placeholder="任务名称" clearable filterable class="filter-item">
        <el-option v-for="item in taskList" :key="item.id" :label="item.taskName" :value="item.id" />
      </el-select>
      <el-select v-model="filters.status" placeholder="发布状态" clearable class="filter-item">
        <el-option label="全部状态" value="" />
        <el-option label="已发布" value="2" />
        <el-option label="未发布" value="0" />
        <el-option label="发布中" value="1" />
      </el-select>
      <el-button type="primary" class="filter-btn" @click="handleSearch">查询</el-button>
      <el-button class="filter-btn" @click="handleReset">重置</el-button>
    </div>
    <div v-loading="loading" class="video-grid">
      <div v-if="!loading && !videoList.length" class="empty-state">
        <div class="empty-icon"><span class="empty-play"></span></div>
        <div class="empty-title">暂无视频</div>
        <div class="empty-description">当前任务下暂无视频</div>
      </div>
      <template v-else>
        <div v-for="item in videoList" :key="item.id" class="video-card" @click="handleVideoClick(item)">
          <div class="video-thumbnail">
            <video :src="item.videoUrl" muted playsinline preload="metadata" aria-label="视频预览"></video>
            <div class="play-btn"><img src="@/assets/images/play-btn.png" alt="播放" /></div>
          </div>
          <div class="video-info">
            <div class="video-status-row">
              <span class="video-status" :class="getStatusClass(item.status)">{{ item.status }}</span>
              <button v-if="isPublished(item.status)" type="button" class="view-link-button"
                @click.stop="handleViewLink(item)">查看链接</button>
            </div>
            <div class="video-task-name">{{ item.taskName }}</div>
            <div class="video-time">{{ item.publishTime }}</div>
          </div>
        </div>
      </template>
    </div>
    <div v-if="total" class="pagination-wrapper">
      <el-pagination v-model:current-page="currentPage" v-model:page-size="pageSize" :total="total"
        layout="total, prev, pager, next" @current-change="handlePageChange" />
    </div>
    <el-dialog v-model="videoDialogVisible" title="视频预览" width="720px" destroy-on-close
      @closed="handleVideoDialogClosed">
      <video v-if="currentVideo" ref="videoPlayer" :src="currentVideo.videoUrl" controls autoplay playsinline
        class="dialog-video"></video>
    </el-dialog>
  </div>
</template>

<script>
export default { name: "PublishVideo" };
</script>

<script setup>
import { nextTick, onMounted, onActivated, ref } from "vue";
import { useRoute } from "vue-router";
import { ElMessage } from "element-plus";
import apiData from "@/api/apiData";

const route = useRoute();
const filters = ref({ taskId: "", status: "" });
const dateRange = ref(null);
const currentPage = ref(1);
const pageSize = ref(16);
const total = ref(0);
const loading = ref(false);
const videoList = ref([]);
const taskList = ref([]);
const videoDialogVisible = ref(false);
const currentVideo = ref(null);
const videoPlayer = ref(null);

const getTaskList = async () => {
  try {
    const response = await apiData.publishTaskList();
    let list = [];
    if (response?.data) {
      if (Array.isArray(response.data)) list = response.data;
      else if (response.data.records) list = response.data.records;
      else if (response.data.list) list = response.data.list;
    }
    taskList.value = list.map(item => ({
      ...item,
      id: String(item.id || item.taskId),
      taskName: item.taskName || item.name || item.task_name
    }));
    // 从任务页跳转过来时，自动填充筛选项并加载视频列表
    const queryTaskId = route.query.taskId;
    if (queryTaskId && taskList.value.find(t => t.id === String(queryTaskId))) {
      filters.value.taskId = String(queryTaskId);
      if (route.query.startTime && route.query.endTime) {
        dateRange.value = [route.query.startTime, route.query.endTime];
      }
      getPublishVideoPage();
    }
  } catch (error) {
    taskList.value = [];
    ElMessage.error("任务列表加载失败");
  }
};

const getPublishVideoPage = async () => {
  if (!filters.value.taskId) {
    videoList.value = [];
    total.value = 0;
    return;
  }
  loading.value = true;
  try {
    const params = {
      pageNum: currentPage.value,
      pageSize: pageSize.value,
      taskId: filters.value.taskId,
      status: filters.value.status,
    };
    if (dateRange.value && dateRange.value.length === 2) {
      params.startTime = dateRange.value[0];
      const endDate = new Date(dateRange.value[1]);
      endDate.setDate(endDate.getDate() + 1);
      params.endTime = endDate.toISOString().split("T")[0];
    }
    const response = await apiData.publishVideoPage(params);
    const pageData = response?.data || {};
    const records = pageData.records || pageData.list || [];
    videoList.value = records;
    total.value = Number(pageData.total || 0);
  } catch (error) {
    videoList.value = [];
    total.value = 0;
    ElMessage.error("视频列表加载失败");
  } finally {
    loading.value = false;
  }
};

const handleSearch = () => {
  currentPage.value = 1;
  getPublishVideoPage();
};
const handleReset = () => {
  dateRange.value = null;
  filters.value.taskId = "";
  filters.value.status = "";
  currentPage.value = 1;
  getPublishVideoPage();
};
const handlePageChange = (page) => { currentPage.value = page; getPublishVideoPage(); };
const handleVideoClick = async (item) => { currentVideo.value = item; videoDialogVisible.value = true; await nextTick(); videoPlayer.value?.play(); };
const handleVideoDialogClosed = () => { if (videoPlayer.value) { videoPlayer.value.pause(); videoPlayer.value.currentTime = 0; } currentVideo.value = null; };
const isPublished = (status) => String(status) === "已发布";
const handleViewLink = (item) => {
  const link = item.douyinUrl;
  if (link) window.open(link, "_blank");
  else ElMessage.info("暂无可查看的链接");
};
const getStatusClass = (status) => ({ "未发布": "unpublished", "已发布": "published", "发布中": "publishing" }[status] || "unpublished");
const getStatusText = (status) => ({ 0: "未发布", 1: "已发布", 2: "发布中" }[status] || "未知");
onMounted(() => { getTaskList(); });

// keep-alive 激活时，检查是否有从任务页传来的参数
onActivated(async () => {
  const queryTaskId = route.query.taskId;
  if (queryTaskId) {
    let needRefresh = false;
    // 更新任务ID
    if (String(filters.value.taskId) !== String(queryTaskId)) {
      filters.value.taskId = String(queryTaskId);
      needRefresh = true;
    }
    // 更新日期范围
    if (route.query.startTime && route.query.endTime) {
      const newRange = [route.query.startTime, route.query.endTime];
      if (!dateRange.value || dateRange.value[0] !== newRange[0] || dateRange.value[1] !== newRange[1]) {
        dateRange.value = newRange;
        needRefresh = true;
      }
    }
    if (needRefresh) {
      currentPage.value = 1;
      getPublishVideoPage();
    }
  }
});
</script>

<style lang="scss" scoped>
.video-publish-page {
  width: 100%;
  min-height: 100%;
  background: #fff;
  padding: 30px;
  border-radius: 25px;
}

.page-header {
  margin-bottom: 16px;
  padding-left: 12px;
  position: relative;
}

.page-header::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 20px;
  background: #409eff;
  border-radius: 0 2px 2px 0;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.page-divider {
  height: 1px;
  background: #e8e8e8;
  margin-bottom: 20px;
}

.filter-bar {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
}

.filter-item {
  width: 210px;
}

.filter-date {
  width: 320px;
}

.filter-btn {
  width: 80px;
}

.video-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 20px;
  min-height: 360px;
}

.empty-state {
  grid-column: 1 / -1;
  min-height: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.empty-icon {
  width: 104px;
  height: 104px;
  border-radius: 50%;
  background: #f2f6fc;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-bottom: 18px;
}

.empty-icon::before {
  content: "";
  position: absolute;
  width: 62px;
  height: 46px;
  border: 4px solid #d9e4f5;
  border-radius: 8px;
  background: #fff;
}

.empty-play {
  position: relative;
  z-index: 1;
  width: 0;
  height: 0;
  border-top: 12px solid transparent;
  border-bottom: 12px solid transparent;
  border-left: 18px solid #a9c8f5;
  margin-left: 5px;
}

.empty-title {
  font-size: 16px;
  color: #606266;
  margin-bottom: 8px;
}

.empty-description {
  font-size: 13px;
  color: #a8abb2;
}

.video-card {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all .3s;
}

.video-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, .1);
  transform: translateY(-2px);
}

.video-thumbnail {
  position: relative;
  width: 100%;
  padding-bottom: 140%;
  background: #f0f0f0;
  overflow: hidden;
}

.video-thumbnail video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.play-btn {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 48px;
  height: 48px;
  opacity: .9;
}

.play-btn img {
  width: 100%;
  height: 100%;
}

.video-info {
  padding: 10px 12px 12px;
}

.video-status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 22px;
  margin-bottom: 6px;
}

.video-status {
  font-size: 13px;
  line-height: 20px;
}

.video-status.published {
  color: #4595FE;
}

.video-status.publishing {
  color: #FF9213;
}

.video-status.unpublished {
  color: #DE0D0D;
}

.view-link-button {
  border: 0;
  padding: 0;
  background: transparent;
  color: #4595FE;
  font-size: 12px;
  cursor: pointer;
}

.video-task-name,
.video-time {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.video-task-name {
  font-size: 13px;
  color: #8491AF;
  line-height: 22px;
}

.video-time {
  font-size: 12px;
  color: #8491AF;
  line-height: 22px;
}

.dialog-video {
  display: block;
  width: 100%;
  max-height: 65vh;
  background: #000;
}

.pagination-wrapper {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}
.filter-bar {
  ::v-deep .el-button+.el-button {
    margin-left: 0;
  }
}

// @media (max-width: 1800px) {
//   .video-grid {
//     grid-template-columns: repeat(7, 1fr);
//   }
// }

// @media (max-width: 1600px) {
//   .video-grid {
//     grid-template-columns: repeat(6, 1fr);
//   }
// }

// @media (max-width: 1400px) {
//   .video-grid {
//     grid-template-columns: repeat(5, 1fr);
//   }
// }

// @media (max-width: 1200px) {
//   .video-grid {
//     grid-template-columns: repeat(4, 1fr);
//   }
// }

// @media (max-width: 992px) {
//   .video-grid {
//     grid-template-columns: repeat(3, 1fr);
//   }
// }

// @media (max-width: 768px) {
//   .video-grid {
//     grid-template-columns: repeat(2, 1fr);
//   }
// }

// @media (max-width: 480px) {
//   .video-grid {
//     grid-template-columns: 1fr;
//   }
// }
</style>
